import { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useSession } from "../auth/AuthProvider";
import {
  formatDigits,
  isoToJalali,
  jalaliMonthLength,
  jalaliMonthName,
  jalaliToIso,
  kabulToday,
  weekdayIndexSatFirst,
} from "../lib/format";
import { useI18n } from "../i18n";
import type { StringKey } from "../i18n/fa";
import { PageHeader } from "../components/ui";

interface BookingDoc {
  id: string;
  date: string;
  shift: "MORNING" | "EVENING" | "FULL_DAY";
  status: string;
  guestCount: number;
}

const SHIFT_KEY: Record<string, StringKey> = {
  MORNING: "shift_morning",
  EVENING: "shift_evening",
  FULL_DAY: "shift_full_day",
};
const SHIFT_ORDER: Record<string, number> = { MORNING: 0, EVENING: 1, FULL_DAY: 2 };

const STATUS_KEY: Record<string, StringKey> = {
  pending_request: "booking_status_pending_request",
  pending_deposit: "booking_status_pending_deposit",
  confirmed: "booking_status_confirmed",
  completed: "booking_status_completed",
  cancelled: "booking_status_cancelled",
};

/** The Afghan week runs شنبه → جمعه; جمعه is the weekend (and the busiest wedding day). */
const WEEKDAY_KEYS: StringKey[] = [
  "weekday_sat",
  "weekday_sun",
  "weekday_mon",
  "weekday_tue",
  "weekday_wed",
  "weekday_thu",
  "weekday_fri",
];

const TINT_FULL = "rgba(186, 26, 26, 0.07)";
const TINT_PARTIAL = "rgba(154, 103, 0, 0.08)";
const HAIRLINE = "1px solid #E8E6DF";

/**
 * Month reservation grid — the operational heart of the owner panel (docs/06 §5).
 *
 * Built on the **Jalali** month the owner thinks in (حمل … حوت), not the
 * Gregorian one: every cell's stored date comes from jalaliToIso, which is
 * pure arithmetic, so no timezone can shift a booking onto the wrong day.
 * "Today" is Kabul's today.
 */
export function CalendarPage() {
  const { orgId } = useSession();
  const { t, dir } = useI18n();
  const today = kabulToday();
  const todayJ = isoToJalali(today);
  const [view, setView] = useState({ jy: todayJ.jy, jm: todayJ.jm });
  const [bookings, setBookings] = useState<BookingDoc[]>([]);

  const length = jalaliMonthLength(view.jy, view.jm);
  const monthStart = jalaliToIso(view.jy, view.jm, 1);
  const monthEnd = jalaliToIso(view.jy, view.jm, length);

  useEffect(() => {
    if (!orgId) return;
    // Realtime: reservation changes from any device appear instantly.
    const q = query(
      collection(db, "bookings"),
      where("orgId", "==", orgId),
      where("date", ">=", monthStart),
      where("date", "<=", monthEnd),
    );
    return onSnapshot(q, (snap) => {
      setBookings(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as Omit<BookingDoc, "id">) }))
          .filter((b) => b.status !== "cancelled"),
      );
    });
  }, [orgId, monthStart, monthEnd]);

  const byDate = useMemo(() => {
    const map = new Map<string, BookingDoc[]>();
    for (const booking of bookings) {
      map.set(booking.date, [...(map.get(booking.date) ?? []), booking]);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (SHIFT_ORDER[a.shift] ?? 9) - (SHIFT_ORDER[b.shift] ?? 9));
    }
    return map;
  }, [bookings]);

  function moveMonth(delta: number) {
    setView((v) => {
      let jm = v.jm + delta;
      let jy = v.jy;
      if (jm < 1) {
        jm = 12;
        jy -= 1;
      } else if (jm > 12) {
        jm = 1;
        jy += 1;
      }
      return { jy, jm };
    });
  }

  const isCurrentMonth = view.jy === todayJ.jy && view.jm === todayJ.jm;
  // Leading blanks so day 1 sits under its real weekday.
  const lead = weekdayIndexSatFirst(monthStart);
  const cells: ({ jd: number; iso: string } | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length }, (_, i) => ({ jd: i + 1, iso: jalaliToIso(view.jy, view.jm, i + 1) })),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  // In RTL "previous" points right; in LTR it points left.
  const PrevIcon = dir === "rtl" ? ChevronRightIcon : ChevronLeftIcon;
  const NextIcon = dir === "rtl" ? ChevronLeftIcon : ChevronRightIcon;

  return (
    <>
      <PageHeader
        title={t("nav_calendar")}
        subtitle={t("calendar_month_summary", { count: bookings.length })}
        action={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton onClick={() => moveMonth(-1)} aria-label={t("calendar_prev_month")}>
              <PrevIcon />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: 140, textAlign: "center" }}>
              {jalaliMonthName(view.jm)} {formatDigits(view.jy)}
            </Typography>
            <IconButton onClick={() => moveMonth(1)} aria-label={t("calendar_next_month")}>
              <NextIcon />
            </IconButton>
            <Button
              size="small"
              variant="outlined"
              disabled={isCurrentMonth}
              onClick={() => setView({ jy: todayJ.jy, jm: todayJ.jm })}
            >
              {t("calendar_today")}
            </Button>
          </Stack>
        }
      />

      <Stack direction="row" spacing={2} mb={1.5} flexWrap="wrap" useFlexGap>
        <Legend color="transparent" label={t("calendar_legend_free")} />
        <Legend color={TINT_PARTIAL} label={t("calendar_legend_partial")} />
        <Legend color={TINT_FULL} label={t("calendar_legend_full")} />
      </Stack>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Box display="grid" gridTemplateColumns="repeat(7, minmax(0, 1fr))">
          {WEEKDAY_KEYS.map((key, i) => (
            <Box
              key={key}
              sx={{
                py: 1,
                textAlign: "center",
                fontSize: 12,
                fontWeight: 700,
                color: i === 6 ? "secondary.main" : "text.secondary",
                bgcolor: "#FAF9F5",
                borderBottom: HAIRLINE,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {t(key)}
            </Box>
          ))}

          {cells.map((cell, index) => {
            const borderEnd = (index + 1) % 7 !== 0 ? HAIRLINE : "none";
            const borderBottom = index < cells.length - 7 ? HAIRLINE : "none";
            if (!cell) {
              return (
                <Box
                  key={`blank-${index}`}
                  sx={{ bgcolor: "#FBFAF7", borderInlineEnd: borderEnd, borderBottom }}
                />
              );
            }
            const dayBookings = byDate.get(cell.iso) ?? [];
            const full =
              dayBookings.some((b) => b.shift === "FULL_DAY") ||
              (dayBookings.some((b) => b.shift === "MORNING") &&
                dayBookings.some((b) => b.shift === "EVENING"));
            const isToday = cell.iso === today;
            const isPast = cell.iso < today;
            return (
              <Box
                key={cell.iso}
                sx={{
                  minHeight: { xs: 70, md: 96 },
                  p: { xs: 0.5, md: 1 },
                  borderInlineEnd: borderEnd,
                  borderBottom,
                  bgcolor: full ? TINT_FULL : dayBookings.length > 0 ? TINT_PARTIAL : "transparent",
                  opacity: isPast && dayBookings.length === 0 ? 0.55 : 1,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: isToday ? 800 : 600,
                    bgcolor: isToday ? "primary.main" : "transparent",
                    color: isToday ? "primary.contrastText" : "text.primary",
                    mb: 0.5,
                  }}
                >
                  {formatDigits(cell.jd)}
                </Box>
                <Stack spacing={0.5}>
                  {dayBookings.map((b) => (
                    <Tooltip
                      key={b.id}
                      title={t("calendar_day_tooltip", {
                        count: b.guestCount,
                        status: STATUS_KEY[b.status] ? t(STATUS_KEY[b.status]) : b.status,
                      })}
                    >
                      <Chip
                        size="small"
                        label={SHIFT_KEY[b.shift] ? t(SHIFT_KEY[b.shift]) : b.shift}
                        color={b.status === "confirmed" || b.status === "completed" ? "primary" : "warning"}
                        variant={b.status === "confirmed" || b.status === "completed" ? "filled" : "outlined"}
                        sx={{
                          height: 20,
                          fontSize: 11,
                          maxWidth: "100%",
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box sx={{ width: 14, height: 14, borderRadius: 0.75, bgcolor: color, border: HAIRLINE }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}
